<script lang="ts">
  import type { InkMode, PreviewBlock } from '$lib/model/types';
  import { projectStore, selectStep } from '$lib/state/project.svelte';
  import { walkLeaves } from '$lib/model/tree';
  import { triggerRecompute } from '$lib/compute/recompute.svelte';
  import ColorPicker from './ColorPicker.svelte';

  // Ensure layer_order stays in sync with current leaves (add new leaves at
  // end = printed last; drop missing ones).
  $effect(() => {
    const leaves = walkLeaves(projectStore.pipeline);
    const leafIds = new Set(leaves.map((l) => l.id));
    const filtered = projectStore.layer_order.filter((id) => leafIds.has(id));
    const present = new Set(filtered);
    const additions = leaves
      .map((l) => l.id)
      .filter((id) => !present.has(id));
    const next = [...filtered, ...additions];
    if (
      next.length !== projectStore.layer_order.length ||
      next.some((id, i) => id !== projectStore.layer_order[i])
    ) {
      projectStore.layer_order = next;
    }
  });

  // Display order: top of UI list = printed LAST (top of physical stack).
  const display = $derived([...projectStore.layer_order].reverse());

  function leafStep(id: string) {
    return projectStore.pipeline.steps[id];
  }
  function previewOf(id: string): PreviewBlock | undefined {
    return leafStep(id)?.preview;
  }

  function setPreviewField<K extends keyof PreviewBlock>(
    id: string,
    field: K,
    value: PreviewBlock[K],
  ) {
    const step = projectStore.pipeline.steps[id];
    if (!step || !step.preview) return;
    step.preview[field] = value;
    triggerRecompute();
  }

  function move(displayIdx: number, delta: -1 | 1) {
    const realIdx = projectStore.layer_order.length - 1 - displayIdx;
    const targetReal = realIdx - delta; // moving UP in UI = later printed
    if (targetReal < 0 || targetReal >= projectStore.layer_order.length) return;
    const next = [...projectStore.layer_order];
    const [moved] = next.splice(realIdx, 1);
    if (!moved) return;
    next.splice(targetReal, 0, moved);
    projectStore.layer_order = next;
    triggerRecompute();
  }
</script>

<section class="panel">
  <h3>Layers <span class="hint">top = printed last</span></h3>
  {#if display.length === 0}
    <p class="empty">No exportable layers yet. Add a halftone or gradient band.</p>
  {:else}
    <ul class="list">
      {#each display as id, displayIdx (id)}
        {@const step = leafStep(id)}
        {@const preview = previewOf(id)}
        {#if step && preview}
          <li class="row" class:disabled={!preview.enabled}>
            <input
              type="checkbox"
              checked={preview.enabled}
              onchange={(e) =>
                setPreviewField(
                  id,
                  'enabled',
                  (e.target as HTMLInputElement).checked,
                )}
              aria-label="Enable layer"
            />
            <ColorPicker
              value={preview.color}
              onchange={(v) => setPreviewField(id, 'color', v)}
              ariaLabel="Ink color"
            />
            <button class="name" onclick={() => selectStep(id)}>{step.name}</button>
            <select
              value={preview.ink_mode}
              onchange={(e) =>
                setPreviewField(
                  id,
                  'ink_mode',
                  (e.target as HTMLSelectElement).value as InkMode,
                )}
              aria-label="Ink mode"
            >
              <option value="opaque">opaque</option>
              <option value="transparent">transparent</option>
            </select>
            <input
              type="number"
              min="0"
              max="100"
              value={preview.opacity}
              oninput={(e) =>
                setPreviewField(
                  id,
                  'opacity',
                  parseInt((e.target as HTMLInputElement).value, 10),
                )}
              class="opacity"
              aria-label="Opacity %"
            />
            <button
              class="ico"
              onclick={() => move(displayIdx, -1)}
              aria-label="Move up (print later)">↑</button
            >
            <button
              class="ico"
              onclick={() => move(displayIdx, 1)}
              aria-label="Move down (print earlier)">↓</button
            >
          </li>
        {/if}
      {/each}
    </ul>
  {/if}
</section>

<style>
  .panel {
    display: flex;
    flex-direction: column;
    gap: var(--gap-s);
  }
  h3 {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-dim);
  }
  .hint {
    font-size: 10px;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
    font-family: var(--font-mono);
    color: var(--text-dim);
  }
  .empty {
    color: var(--text-dim);
    font-size: 12px;
    margin: 0;
  }
  .list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .row {
    display: grid;
    grid-template-columns: 18px 28px 1fr auto 56px 22px 22px;
    gap: 4px;
    align-items: center;
    padding: 4px;
    background: var(--surface-2);
    border-radius: 3px;
  }
  .row.disabled {
    opacity: 0.45;
  }
  .row.disabled .name {
    text-decoration: line-through;
  }
  .row input[type='checkbox'] {
    accent-color: var(--accent);
  }
  .name {
    text-align: left;
    background: transparent;
    border: none;
    color: var(--text);
    font-size: 12px;
    padding: 2px 4px;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .name:hover {
    color: var(--accent);
  }
  select {
    font-size: 11px;
    padding: 2px;
  }
  .opacity {
    font-family: var(--font-mono);
    font-size: 11px;
    text-align: right;
  }
  .ico {
    background: transparent;
    border: 1px solid var(--border);
    color: var(--text-dim);
    font-size: 12px;
    line-height: 1;
    padding: 2px;
  }
  .ico:hover {
    color: var(--accent);
    border-color: var(--accent);
  }
</style>
